import { useEffect, useState } from "react";
import { contractOfficerAPI, departmentAPI, officeAPI } from "../services/api";
import { StatusBadge, Modal, Btn, Th, Td, ActionBtns, ThActions, ConfirmDialog, Toast, Can, errMessage, TableScroll, Pagination } from "../components/shared/index";
import { useLanguage } from "../context/LanguageContext";
import {
  MdSearch, MdWarning, MdPerson, MdBadge, MdCake, MdPhone, MdEmail,
  MdAccountBalance, MdBarChart, MdCalendarToday, MdAccessTime,
  MdAssignment, MdNotes, MdEdit, MdDelete,
} from "react-icons/md";
const BROWN  = "var(--brown)";
const BORDER = "var(--border)";
const BG     = "var(--cream)";
const MUTED  = "var(--muted)";
const KH     = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO     = "'Moul','Noto Sans Khmer',serif";

const STATUS_LABEL = {
  ACTIVE:           "កំពុងបម្រើការ",
  INACTIVE:         "មិនសកម្ម",
  SUSPENDED:        "ផ្អាកការងារ",
  ON_LEAVE:         "ឈប់សម្រាក",
  RESIGNED:         "បានលាឈប់",
  CONTRACT_EXPIRED: "កិច្ចសន្យាផុត",
};
const GENDER_LABEL = { M:"ប្រុស", F:"ស្រី", MONK:"បព្វជិត" };

const inputStyle = {
  width:"100%", padding:"9px 12px",
  border:`1px solid ${BORDER}`, borderRadius:8,
  fontSize:13, outline:"none", background:"var(--surface)",
  fontFamily:KH, boxSizing:"border-box",
};

const toDisplay = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0,10).split("-");
  return `${d}-${m}-${y}`;
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000*60*60*24));
  return diff;
};

const API_ORIGIN = "https://ginbecc-backend.onrender.com";
const toImageUrl = (raw) => {
  if (typeof raw !== "string" || !raw.trim()) return null;
  if (/^https?:\/\//.test(raw)) return raw;
  if (raw.startsWith("/")) return API_ORIGIN + raw;
  return null;
};

function SkeletonRow() {
  return (
    <tr>
      {Array(10).fill(0).map((_,i) => (
        <td key={i} style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
          <div style={{ height:14, background:"var(--border)", borderRadius:4 }} />
        </td>
      ))}
    </tr>
  );
}

function SectionTitle({ children }) {
  return (
    <p style={{ fontSize:11, color:MUTED, fontWeight:600, margin:"14px 0 8px",
                borderBottom:`1px solid ${BORDER}`, paddingBottom:4, fontFamily:KH }}>
      {children}
    </p>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div style={{ display:"flex", alignItems:"flex-start", gap:10,
                  background:BG, padding:"9px 14px", borderRadius:8, marginBottom:6 }}>
      <span style={{ flexShrink:0, marginTop:2, color:MUTED, display:"flex", alignItems:"center" }}>{icon}</span>
      <div>
        <p style={{ fontSize:10, color:MUTED, marginBottom:2, fontFamily:KH }}>{label}</p>
        <div style={{ fontSize:13, fontWeight:500, color:"var(--text)", fontFamily:KH }}>
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

function ContractBadge({ endDate, status }) {
  const days = daysUntil(endDate);
  if (days !== null && days <= 30 && days > 0) {
    return (
      <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11,
                     fontWeight:600, background:"#FEF3C7", color:"#92400E",
                     fontFamily:KH, display:"inline-flex", alignItems:"center", gap:4 }}>
        <MdWarning size={12}/> ផុតក្នុង {days} ថ្ងៃ
      </span>
    );
  }
  if (days !== null && days <= 0) {
    return <StatusBadge status="កិច្ចសន្យាផុត" />;
  }
  return <StatusBadge status={STATUS_LABEL[status] || status} />;
}

export default function ContractOfficer() {
  const { t } = useLanguage();
  const [list,         setList]         = useState([]);
  const [departments,  setDepartments]  = useState([]);
  const [offices,      setOffices]      = useState([]);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deptFilter,   setDeptFilter]   = useState("");
  const [modal,        setModal]        = useState(null);
  const [form,         setForm]         = useState({});
  const [selected,     setSelected]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [editId,       setEditId]       = useState(null);
  const [originalCode, setOriginalCode] = useState("");
  const [page,         setPage]         = useState(0);
  const [pageSize,     setPageSize]     = useState(25);
  const [totalPages,   setTotalPages]   = useState(1);
  const [totalItems,   setTotalItems]   = useState(0);
  const [deleteId,     setDeleteId]     = useState(null);
  const [toast,        setToast]        = useState(null);
  const [err,          setErr]          = useState(null);
  const [imageFile,    setImageFile]    = useState(null);
  const [lightbox,     setLightbox]     = useState(null);

  const getStatus = (obj) => obj?.status || obj?.statusCode || "";

  const load = async () => {
    setLoading(true);
    try {
      const res  = await contractOfficerAPI.getAll(
        page, pageSize,
        statusFilter || undefined,
        deptFilter   || undefined
      );
      const data = res?.data?.data ?? res?.data;
      setList(data?.content || []);
      setTotalPages(data?.totalPages    || 1);
      setTotalItems(data?.totalElements || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    departmentAPI.getAll().then(r => {
      const d = r?.data?.data ?? r?.data;
      const list = Array.isArray(d) ? d : [];
      setDepartments(list.map(dep => ({
        ...dep,
        id:             dep.departmentId ?? dep.id,
        departmentName: dep.departmentName ?? dep.name,
      })));
    }).catch(() => {});
    officeAPI.getAll().then(r => {
      const d = r?.data?.data ?? r?.data;
      setOffices((Array.isArray(d) ? d : []).map(o => ({
        ...o, id: o.officeId ?? o.id,
        officeName: o.officeName ?? o.name,
      })));
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [page, pageSize, statusFilter, deptFilter]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        departmentId:        form.departmentId ? parseInt(form.departmentId, 10) : null,
        officeId:            form.officeId ? parseInt(form.officeId, 10) : null,
        contractOfficerCode: form.contractOfficerCode || "",
        fullNameKh:          form.fullNameKh    || "",
        fullNameEn:          form.fullNameEn    || "",
        gender:              form.gender        || "M",
        dob:                 form.dob           || null,
        phone:               form.phone         || "",
        email:               form.email         || "",
        jobLevel:            form.jobLevel      || "",
        startDate:           form.startDate     || null,
        endDate:             form.endDate       || null,
        note:                form.note          || "",
        accountingCode:      form.accountingCode|| "",
        statusCode:          form.status        || "ACTIVE",
      };

      let contractOfficerId = editId;
      if (editId) {
        await contractOfficerAPI.update(editId, payload);
      } else {
        const res = await contractOfficerAPI.create(payload);
        const created = res?.data?.data ?? res?.data;
        contractOfficerId = created?.contractOfficerId ?? created?.id;
      }

      if (imageFile && contractOfficerId) {
        try {
          await contractOfficerAPI.uploadImage(contractOfficerId, imageFile);
        } catch (uploadErr) {
          console.warn("Profile image upload failed:", uploadErr.message);
          setErr("រក្សាទុកមន្ត្រីកិច្ចសន្យារួចហើយ ប៉ុន្តែការបញ្ចូលរូបភាពបរាជ័យ: " + errMessage(uploadErr));
        }
      }

      await load();
      setToast(editId ? "កែប្រែមន្ត្រីកិច្ចសន្យាបានជោគជ័យ!" : "បន្ថែមមន្ត្រីកិច្ចសន្យាបានជោគជ័យ!");
      setModal(null); setForm({}); setEditId(null);
      setOriginalCode(""); setImageFile(null);
    } catch (e) {
      setErr(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => setDeleteId(id);

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await contractOfficerAPI.delete(id);
      await load();
      if ((selected?.contractOfficerId ?? selected?.id) === id) setModal(null);
      setToast(t("លុបមន្ត្រីកិច្ចសន្យាបានជោគជ័យ!"));
    } catch (e) { setErr(errMessage(e)); }
  };

  const openEdit = (s) => {
    const id = s.contractOfficerId ?? s.id;
    setEditId(id);
    setOriginalCode(s.contractOfficerCode || "");
    setForm({
      departmentId:        (s.departmentId || s.department?.departmentId) ? String(s.departmentId || s.department?.departmentId) : "",
      officeId:            (s.officeId || s.office?.officeId) ? String(s.officeId || s.office?.officeId) : "",
      contractOfficerCode: s.contractOfficerCode || "",
      fullNameKh:          s.fullNameKh   || "",
      fullNameEn:          s.fullNameEn   || "",
      gender:              s.gender       || "M",
      dob:                 s.dob?.slice(0,10) || "",
      phone:               s.phone        || "",
      email:               s.email        || "",
      jobLevel:            s.jobLevel     || "",
      startDate:           s.startDate?.slice(0,10) || "",
      endDate:             s.endDate?.slice(0,10)   || "",
      note:                s.note         || "",
      accountingCode:      s.accountingCode || "",
      status:              getStatus(s)   || "ACTIVE",
      imageSrc:            toImageUrl(s.profileImageUrl),
    });
    setImageFile(null);
    setModal("add");
    contractOfficerAPI.getProfileImage(id)
      .then(res => {
        const raw = res?.data?.downloadUrl ?? res?.data?.imageUrl ?? res?.data?.url ?? res?.data;
        const url = toImageUrl(raw);
        if (url) setForm(prev => ({ ...prev, imageSrc: url }));
        else if (raw) console.warn("profile-image: unusable value from API:", raw);
      })
      .catch((e) => console.warn("profile-image request failed:", e.message));
  };

  const officesForDept = form.departmentId
    ? offices.filter(o =>
        String(o.departmentId ?? o.department?.departmentId ?? "") === String(form.departmentId))
    : [];

  const filtered = list.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [s.contractOfficerCode, s.fullNameKh, s.fullNameEn, s.departmentName, s.officeName]
      .filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)",
                    gap:12, marginBottom:20 }}>
        {[
          { label:"មន្ត្រីកិច្ចសន្យាសរុប", value:totalItems, color:BROWN },
          { label:"កំពុងបម្រើការ",           value:list.filter(s=>getStatus(s)==="ACTIVE").length,           color:"#10B981" },
          { label:"ផុតកិច្ចសន្យាក្នុង ៣០ ថ្ងៃ",value:list.filter(s=>{ const d=daysUntil(s.endDate); return d!==null&&d>=0&&d<=30; }).length, color:"#F59E0B" },
          { label:"ផុតកិច្ចសន្យាហើយ",        value:list.filter(s=>{ const d=daysUntil(s.endDate); return d!==null&&d<0; }).length, color:"#EF4444" },
        ].map((s,i) => (
          <div key={i} style={{ background:"var(--surface)", borderRadius:10,
                                 padding:"14px 18px", border:`1px solid ${BORDER}` }}>
            <p style={{ margin:"0 0 6px", fontSize:12, color:MUTED, fontFamily:KH }}>{s.label}</p>
            <p style={{ margin:0, fontSize:26, fontWeight:700,
                        color:s.color, fontFamily:MO }}>
              {loading ? "..." : s.value}
            </p>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", gap:12, marginBottom:16,
                    alignItems:"center", flexWrap:"wrap" }}>
        <div style={{ flex:1, minWidth:200, display:"flex", alignItems:"center", gap:8,
                      background:"var(--surface)", border:`1px solid ${BORDER}`,
                      borderRadius:8, padding:"0 12px" }}>
          <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t("ស្វែងរកឈ្មោះ ឬ លេខ....")}
            style={{ border:"none", outline:"none", background:"transparent",
                     flex:1, padding:"9px 0", fontSize:13, fontFamily:KH }} />
        </div>

        <select value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`, borderRadius:8,
                   fontSize:13, outline:"none", background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("ស្ថានភាពទាំងអស់")}</option>
          <option value="ACTIVE">{t("កំពុងបម្រើការ")}</option>
          <option value="INACTIVE">{t("មិនសកម្ម")}</option>
          <option value="SUSPENDED">{t("ផ្អាកការងារ")}</option>
          <option value="RESIGNED">{t("បានលាឈប់")}</option>
          <option value="CONTRACT_EXPIRED">{t("កិច្ចសន្យាផុត")}</option>
        </select>

        <select value={deptFilter}
          onChange={e => { setDeptFilter(e.target.value); setPage(0); }}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`, borderRadius:8,
                   fontSize:13, outline:"none", background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("នាយកដ្ឋានទាំងអស់")}</option>
          {departments.map(d => (
            <option key={d.id} value={d.id}>{d.departmentName}</option>
          ))}
        </select>

        <Can perm="contract" action="create">
        <button onClick={() => {
            setEditId(null); setOriginalCode("");
            setForm({ status:"ACTIVE", gender:"M" }); setModal("add");
          }}
          style={{ padding:"9px 18px", background:BROWN, color:"#fff",
                   border:"none", borderRadius:8, cursor:"pointer",
                   fontSize:13, fontWeight:500, whiteSpace:"nowrap", fontFamily:KH }}>
          {t("+ បន្ថែមមន្ត្រីកិច្ចសន្យា")}
        </button>
        </Can>
      </div>

      {!loading && (
        <p style={{ fontSize:12, color:MUTED, marginBottom:10, fontFamily:KH }}>
          {t("សរុប:")} <strong style={{ color:BROWN }}>{totalItems}</strong> {t("នាក់")}
        </p>
      )}

      <div style={{ background:"var(--surface)", borderRadius:10,
                    border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr>
              <Th>{t("លេខ")}</Th>
              <Th>{t("ភេទ")}</Th>
              <Th>{t("ឈ្មោះខ្មែរ")}</Th>
              <Th>{t("ឈ្មោះអង់គ្លេស")}</Th>
              <Th>{t("ការិយាល័យ")}</Th>
              <Th>{t("នាយកដ្ឋាន")}</Th>
              <Th>{t("ចាប់ផ្តើម")}</Th>
              <Th>{t("បញ្ចប់")}</Th>
              <Th>{t("ស្ថានភាព")}</Th>
              <ThActions perm="contract" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3,4,5].map(i => <SkeletonRow key={i} />)
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ padding:"32px", textAlign:"center",
                                          color:MUTED, fontSize:13, fontFamily:KH }}>
                  {t("គ្មានទិន្នន័យ")}
                </td>
              </tr>
            ) : filtered.map(s => (
              <tr key={s.contractOfficerId ?? s.id}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                <Td muted>{s.contractOfficerCode || s.contractOfficerId || s.id}</Td>
                <Td>{GENDER_LABEL[s.gender] || s.gender || "—"}</Td>
                <Td>{s.fullNameKh || "—"}</Td>
                <Td muted>{s.fullNameEn || "—"}</Td>
                <Td muted>{s.officeName || "—"}</Td>
                <Td>{s.departmentName || "—"}</Td>
                <Td muted>{toDisplay(s.startDate)}</Td>
                <Td muted>
                  <span style={{
                    color: daysUntil(s.endDate) !== null && daysUntil(s.endDate) <= 30
                      ? "#F59E0B" : "inherit"
                  }}>
                    {toDisplay(s.endDate)}
                  </span>
                </Td>
                <Td>
                  <ContractBadge endDate={s.endDate} status={getStatus(s)} />
                </Td>
                <ActionBtns perm="contract"
                  onView={() => {
                    setSelected({ ...s, imageSrc: toImageUrl(s.profileImageUrl) });
                    setModal("view");
                    contractOfficerAPI.getProfileImage(s.contractOfficerId ?? s.id)
                      .then(res => {
                        const raw = res?.data?.downloadUrl ?? res?.data?.imageUrl ?? res?.data?.url ?? res?.data;
                        const url = toImageUrl(raw);
                        if (url) setSelected(prev => prev ? { ...prev, imageSrc: url } : prev);
                        else if (raw) console.warn("profile-image: unusable value from API:", raw);
                      })
                      .catch((e) => console.warn("profile-image request failed:", e.message));
                  }}
                  onEdit={() => openEdit(s)}
                  onDelete={() => handleDelete(s.contractOfficerId ?? s.id)}
                />
              </tr>
            ))}
          </tbody>
        </TableScroll>

        <Pagination
          page={page} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

      <ConfirmDialog
        open={deleteId !== null}
        message={t("តើអ្នកពិតជាចង់លុបមន្ត្រីកិច្ចសន្យានេះមែនទេ?")}
        onConfirm={doDelete}
        onCancel={() => setDeleteId(null)}
      />
      <Toast message={toast} onHide={() => setToast(null)} />
      <Toast message={err} type="error" onHide={() => setErr(null)} />

      {modal === "add" && (
        <Modal
          key={editId || "new"}
          title={editId ? "កែប្រែមន្ត្រីកិច្ចសន្យា" : "បន្ថែមមន្ត្រីកិច្ចសន្យា"}
          subtitle={t("បំពេញព័ត៌មានខាងក្រោម")}
          onClose={() => { setModal(null); setForm({}); setEditId(null);
                           setOriginalCode(""); setImageFile(null); }}
        >
          <SectionTitle>{t("រូបភាពប្រវត្តិរូប")}</SectionTitle>
          <div style={{ marginBottom:14 }}>
            <div style={{ display:"flex", alignItems:"center", gap:16 }}>
              <div style={{ width:84, height:84, borderRadius:"50%", overflow:"hidden",
                            border:`2px solid ${BORDER}`, background:BG, flexShrink:0,
                            display:"flex", alignItems:"center", justifyContent:"center" }}>
                {imageFile
                  ? <img src={URL.createObjectURL(imageFile)} alt="preview"
                      style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                  : form.imageSrc
                  ? <img src={form.imageSrc} alt="current"
                      onClick={() => setLightbox(form.imageSrc)}
                      style={{ width:"100%", height:"100%", objectFit:"cover", cursor:"zoom-in" }} />
                  : <MdPerson size={40} color={MUTED} />}
              </div>
              <div>
                <label style={{ display:"inline-block", padding:"8px 16px",
                                background:BG, border:`1px solid ${BORDER}`,
                                borderRadius:8, cursor:"pointer", fontSize:13,
                                color:BROWN, fontFamily:KH }}>
                  {t("ជ្រើសរើសរូបភាព")}
                  <input type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={e => setImageFile(e.target.files[0] || null)}
                    style={{ display:"none" }} />
                </label>
                {imageFile && (
                  <button type="button" onClick={() => setImageFile(null)}
                    style={{ marginLeft:8, background:"#FEE2E2", border:"none",
                             borderRadius:6, padding:"6px 12px", cursor:"pointer",
                             color:"#991B1B", fontSize:12, fontFamily:KH }}>
                    {t("លុប")}
                  </button>
                )}
                <p style={{ fontSize:11, color:MUTED, marginTop:6, fontFamily:KH }}>
                  {imageFile ? imageFile.name : "JPG, PNG, WEBP"}
                </p>
              </div>
            </div>
          </div>

          <SectionTitle>{t("ព័ត៌មានអត្តសញ្ញាណ")}</SectionTitle>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:12, color:MUTED,
                            marginBottom:4, fontFamily:KH }}>
              {t("លេខកូដមន្ត្រីកិច្ចសន្យា")} <span style={{color:"#EF4444"}}>*</span>
            </label>
            <input value={form.contractOfficerCode||""}
              onChange={e => f("contractOfficerCode", e.target.value)}
              placeholder={t("ឧ. CON-001")}
              style={inputStyle} />
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED,
                              marginBottom:4, fontFamily:KH }}>{t("ឈ្មោះពេញ ខ្មែរ")} <span style={{color:"#EF4444"}}>*</span></label>
              <input value={form.fullNameKh||""}
                onChange={e => f("fullNameKh", e.target.value)}
                placeholder={t("ឧ. ចាន់ ដារ៉ា")} style={inputStyle} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED,
                              marginBottom:4, fontFamily:KH }}>{t("ឈ្មោះពេញអង់គ្លេស")}</label>
              <input value={form.fullNameEn||""}
                onChange={e => f("fullNameEn", e.target.value)}
                placeholder="e.g. Chan Dara" style={inputStyle} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED,
                              marginBottom:4, fontFamily:KH }}>{t("ភេទ")}</label>
              <select value={form.gender||"M"} onChange={e => f("gender",e.target.value)}
                style={inputStyle}>
                <option value="M">{t("ប្រុស")}</option>
                <option value="F">{t("ស្រី")}</option>
                <option value="MONK">{t("បព្វជិត")}</option>
              </select>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED,
                              marginBottom:4, fontFamily:KH }}>{t("ថ្ងៃខែឆ្នាំកំណើត")}</label>
              <input type="date" value={form.dob||""}
                onChange={e => f("dob",e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED,
                              marginBottom:4, fontFamily:KH }}>{t("ទូរស័ព្ទ")}</label>
              <input value={form.phone||""}
                onChange={e => f("phone",e.target.value)}
                placeholder="0xx xxx xxx" style={inputStyle} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED,
                              marginBottom:4, fontFamily:KH }}>{t("អ៊ីមែល")}</label>
              <input type="email" value={form.email||""}
                onChange={e => f("email",e.target.value)}
                placeholder="example@email.com" style={inputStyle} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED,
                              marginBottom:4, fontFamily:KH }}>{t("ស្ថានភាព")} <span style={{color:"#EF4444"}}>*</span></label>
              <select value={form.status||"ACTIVE"} onChange={e => f("status",e.target.value)}
                style={inputStyle}>
                <option value="ACTIVE">{t("កំពុងបម្រើការ")}</option>
                <option value="INACTIVE">{t("មិនសកម្ម")}</option>
                <option value="SUSPENDED">{t("ផ្អាកការងារ")}</option>
                <option value="ON_LEAVE">{t("ឈប់សម្រាក")}</option>
                <option value="RESIGNED">{t("បានលាឈប់")}</option>
                <option value="CONTRACT_EXPIRED">{t("កិច្ចសន្យាផុត")}</option>
              </select>
            </div>
          </div>

          <SectionTitle>{t("ព័ត៌មានការងារ")}</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED,
                              marginBottom:4, fontFamily:KH }}>{t("នាយកដ្ឋាន")} <span style={{color:"#EF4444"}}>*</span></label>
              <select value={form.departmentId||""}
                onChange={e => setForm(p => ({ ...p, departmentId: e.target.value, officeId: "" }))}
                style={inputStyle}>
                <option value="">{t("ជ្រើសរើសនាយកដ្ឋាន...")}</option>
                {departments.map(d => (
                  <option key={d.id} value={String(d.id)}>{d.departmentName}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED,
                              marginBottom:4, fontFamily:KH }}>{t("ការិយាល័យ")} <span style={{color:"#EF4444"}}>*</span></label>
              <select value={form.officeId||""}
                disabled={!form.departmentId}
                onChange={e => f("officeId",e.target.value)}
                style={{ ...inputStyle, ...(!form.departmentId ? { background:"#F5F5F5", cursor:"not-allowed" } : {}) }}>
                <option value="">
                  {!form.departmentId
                    ? t("សូមជ្រើសរើសនាយកដ្ឋានជាមុនសិន...")
                    : officesForDept.length === 0
                    ? t("គ្មានការិយាល័យក្នុងនាយកដ្ឋាននេះ")
                    : t("ជ្រើសរើសការិយាល័យ...")}
                </option>
                {officesForDept.map(o => (
                  <option key={o.id} value={String(o.id)}>{o.officeName}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED,
                              marginBottom:4, fontFamily:KH }}>{t("ចំណាត់ថ្នាក់មុខងារ")}</label>
              <input value={form.jobLevel||""}
                onChange={e => f("jobLevel",e.target.value)}
                placeholder={t("ឧ. Junior, Senior, Manager...")}
                style={inputStyle} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED,
                              marginBottom:4, fontFamily:KH }}>{t("ថ្ងៃចាប់ផ្តើម")} <span style={{color:"#EF4444"}}>*</span></label>
              <input type="date" value={form.startDate||""}
                onChange={e => f("startDate",e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED,
                              marginBottom:4, fontFamily:KH }}>{t("ថ្ងៃបញ្ចប់")} <span style={{color:"#EF4444"}}>*</span></label>
              <input type="date" value={form.endDate||""}
                onChange={e => f("endDate",e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:"block", fontSize:12, color:MUTED,
                              marginBottom:4, fontFamily:KH }}>{t("លេខកូដគណនេយ្យ")}</label>
              <input value={form.accountingCode||""}
                onChange={e => f("accountingCode",e.target.value)}
                placeholder={t("ឧ. ACC-001")} style={inputStyle} />
            </div>
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:12, color:MUTED,
                            marginBottom:4, fontFamily:KH }}>{t("កំណត់សម្គាល់")}</label>
            <textarea value={form.note||""}
              onChange={e => f("note",e.target.value)}
              rows={3} placeholder={t("កំណត់សម្គាល់បន្ថែម...")}
              style={{ ...inputStyle, resize:"vertical" }} />
          </div>

          <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:20 }}>
            <Btn variant="outline"
              onClick={() => { setModal(null); setForm({}); setEditId(null);
                               setOriginalCode(""); setImageFile(null); }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleSave} style={{ opacity:saving ? 0.7 : 1 }}>
              {saving ? "កំពុងរក្សាទុក..." : "រក្សាទុក"}
            </Btn>
          </div>
        </Modal>
      )}

      {modal === "view" && selected && (
        <Modal
          title={selected.fullNameKh || "មន្ត្រីកិច្ចសន្យា"}
          subtitle={selected.fullNameEn || ""}
          onClose={() => setModal(null)}
        >
          <div style={{ display:"flex", justifyContent:"center", marginBottom:16 }}>
            {selected.imageSrc ? (
              <img src={selected.imageSrc} alt={selected.fullNameKh || "contract officer"}
                onClick={() => setLightbox(selected.imageSrc)}
                title={t("ចុចដើម្បីមើលរូបពេញ")}
                style={{ width:130, height:130, borderRadius:"50%",
                         objectFit:"cover", border:`3px solid ${BORDER}`,
                         cursor:"zoom-in" }} />
            ) : (
              <div style={{ width:130, height:130, borderRadius:"50%",
                            background:BG, border:`3px solid ${BORDER}`,
                            display:"flex", alignItems:"center",
                            justifyContent:"center" }}>
                <MdPerson size={56} color={MUTED} />
              </div>
            )}
          </div>

          {(() => {
            const days = daysUntil(selected.endDate);
            if (days !== null && days <= 30 && days >= 0) {
              return (
                <div style={{ background:"#FEF3C7", border:"1px solid #F59E0B",
                              borderRadius:8, padding:"10px 14px", marginBottom:14,
                              display:"flex", alignItems:"center", gap:8 }}>
                  <MdWarning size={18} style={{ color:"#92400E" }} />
                  <p style={{ fontSize:12, color:"#92400E", fontFamily:KH }}>
                    {t("កិច្ចសន្យានឹងផុតក្នុង")} <strong>{days} ថ្ងៃ</strong>
                    {" "}({toDisplay(selected.endDate)})
                  </p>
                </div>
              );
            }
            return null;
          })()}

          <SectionTitle>{t("ព័ត៌មានអត្តសញ្ញាណ")}</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 8px" }}>
            <InfoRow icon={<MdBadge size={16}/>}        label={t("លេខកូដមន្ត្រីកិច្ចសន្យា")}
              value={selected.contractOfficerCode || selected.contractOfficerId} />
            <InfoRow icon={<MdPerson size={16}/>}        label={t("ភេទ")}
              value={GENDER_LABEL[selected.gender] || selected.gender} />
            <InfoRow icon={<MdPerson size={16}/>}        label={t("ឈ្មោះពេញ ខ្មែរ")}  value={selected.fullNameKh} />
            <InfoRow icon={<MdPerson size={16}/>}        label={t("ឈ្មោះពេញ អង់គ្លេស")}     value={selected.fullNameEn} />
            <InfoRow icon={<MdCake size={16}/>}          label={t("ថ្ងៃខែឆ្នាំកំណើត")} value={toDisplay(selected.dob)} />
            <InfoRow icon={<MdPhone size={16}/>}         label={t("ទូរស័ព្ទ")}         value={selected.phone} />
            <InfoRow icon={<MdEmail size={16}/>}         label={t("អ៊ីមែល")}           value={selected.email} />
          </div>

          <SectionTitle>{t("ព័ត៌មានការងារ")}</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 8px" }}>
            <InfoRow icon={<MdAccountBalance size={16}/>} label={t("នាយកដ្ឋាន")}       value={selected.departmentName} />
            <InfoRow icon={<MdAccountBalance size={16}/>} label={t("ការិយាល័យ")}        value={selected.officeName} />
            <InfoRow icon={<MdBarChart size={16}/>}        label={t("ចំណាត់ថ្នាក់មុខងារ")} value={selected.jobLevel} />
            <InfoRow icon={<MdCalendarToday size={16}/>}   label={t("ថ្ងៃចាប់ផ្តើម")}     value={toDisplay(selected.startDate)} />
            <InfoRow icon={<MdAccessTime size={16}/>}      label={t("ថ្ងៃបញ្ចប់")}         value={toDisplay(selected.endDate)} />
            <InfoRow icon={<MdBadge size={16}/>}           label={t("លេខកូដគណនេយ្យ")}    value={selected.accountingCode} />
          </div>
          <InfoRow icon={<MdAssignment size={16}/>} label={t("ស្ថានភាព")}
            value={<ContractBadge endDate={selected.endDate}
                     status={getStatus(selected)} />} />
          {selected.note && (
            <InfoRow icon={<MdNotes size={16}/>} label={t("កំណត់សម្គាល់")} value={selected.note} />
          )}

          <SectionTitle>{t("ព័ត៌មានប្រព័ន្ធ")}</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 8px" }}>
            <InfoRow icon={<MdAccessTime size={16}/>} label={t("ថ្ងៃបញ្ចូល")}
              value={toDisplay(selected.createdAt)} />
            <InfoRow icon={<MdEdit size={16}/>}        label={t("ថ្ងៃកែប្រែ")}
              value={toDisplay(selected.updatedAt)} />
          </div>

          <div style={{ marginTop:20, display:"flex",
                        justifyContent:"space-between", gap:8 }}>
            <Can perm="contract" action="edit">
            <Btn variant="outline" onClick={() => openEdit(selected)}
              style={{display:"flex",alignItems:"center",gap:5}}>
              <MdEdit size={14}/> {t("កែប្រែ")}
            </Btn>
            </Can>
            <Can perm="contract" action="delete">
            <Btn variant="danger"
              onClick={() => handleDelete(selected.contractOfficerId ?? selected.id)}
              style={{display:"flex",alignItems:"center",gap:5}}>
              <MdDelete size={14}/> {t("លុប")}
            </Btn>
            </Can>
          </div>
        </Modal>
      )}

      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position:"fixed", inset:0, zIndex:9999,
                   background:"rgba(0,0,0,0.85)", display:"flex",
                   alignItems:"center", justifyContent:"center",
                   padding:24, cursor:"zoom-out" }}>
          <button onClick={() => setLightbox(null)}
            style={{ position:"absolute", top:18, right:22, background:"none",
                     border:"none", color:"#fff", fontSize:34, cursor:"pointer",
                     lineHeight:1 }}>×</button>
          <img src={lightbox} alt="contract officer full"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth:"95%", maxHeight:"95%", objectFit:"contain",
                     borderRadius:8, boxShadow:"0 8px 40px rgba(0,0,0,0.5)" }} />
        </div>
      )}
    </div>
  );
}